import React, { createContext, useState, useContext, useCallback, ReactNode, useEffect } from 'react';
import { API, ROUTES } from '../common/constants/urls';
import { get, post } from '../common/axiosClient';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { UserDTO } from '../components/user-management/auth.type';
import { LookupsService } from '../services/lookups';
import { Analytics } from '../services/analyticsService';

export interface AuthContextType {
  user: UserDTO | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<string | null>;
  logout: () => Promise<void>;
  error: string | null;
  hasRole: (role: string) => boolean;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<UserDTO | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true); // New loading state
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const validateSession = async () => {
      if (location.pathname === ROUTES.LOGIN || location.pathname === ROUTES.SIGNUP) {
        // If the user is on the login page, skip session validation
        setIsLoading(false);
        return;
      }
      try {
        const response = await get('/user/validate-session');
        // console.log('validate = ' + response.user.firstName);
        setIsAuthenticated(response.isAuthenticated);
        setUser(response.user);
        if (response.isAuthenticated && response.user?.id) {
          Analytics.identify(response.user.id, undefined, `${response.user.firstName} ${response.user.lastName}`);
        }
      } catch (error: any) {
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    validateSession();
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<string | null> => {
      try {
        const response = await post(`${API.LOGIN}`, { email, password });
        setIsAuthenticated(true);
        setUser(response.user);
        if (response.user?.id) {
          Analytics.identify(response.user.id, email, `${response.user.firstName} ${response.user.lastName}`);
        }
        navigate(ROUTES.HOME);
        LookupsService.refreshAllLookups();
        return null;
      } catch (error: any) {
        const message = error.message || error.response?.data?.message;
        toast.error('Login failed: ' + message);
        console.error('Login failed: ', message);
        return message;
      }
    },
    [navigate],
  );

  const logout = useCallback(async () => {
    try {
      await post(`${API.LOGOUT}`, {});
      Analytics.reset();
      setIsAuthenticated(false);
      toast.success('Logout successful!');
      navigate(ROUTES.LOGIN);
    } catch (error: any) {
      const errorMessage = error.response?.status === 401 ? 'Session expired or already logged out' : 'Logout failed!';
      console.error(errorMessage, error);
      toast.error(errorMessage);
      setIsAuthenticated(false);
      navigate(ROUTES.HOME);
    }
  }, [navigate]);

  // Role checking functions
  const hasRole = (role: string): boolean => {
    return user?.roles.includes(role) ?? false;
  };

  const contextValue = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    error,
    hasRole,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
